export const CHART_CSV_MAX_DATA_POINTS = 10_000;
export const CHART_CSV_MAX_BYTES = 10 * 1024 * 1024; // 10 MB

// Series keys whose chart data lives in the 'count' field rather than the key itself.
// Mirrors the dataKey mapping in ActiveUsersChart.tsx.
const COUNT_ALIAS_KEYS = new Set(['overall', 'watched', 'licensed', 'percentage']);

export type CsvExportFailureReason =
  | 'no_data'
  | 'data_point_limit_exceeded'
  | 'file_size_exceeded'
  | 'unexpected_chart_state';

export type CsvExportResult =
  | { ok: true; csv: string }
  | { ok: false; reason: CsvExportFailureReason };

export interface ChartSeriesConfig {
  key: string;
  label?: string;
  checked?: boolean;
  fixed?: boolean;
  fixedFor?: string[];
  disabled?: boolean;
}

export interface ChartItemData {
  date: string;
  count: number;
  [key: string]: unknown;
}

export interface ChartCsvOptions {
  chartTitle: string;
  chartDataType: string;
  chartType: string;
  filters: {
    date_from?: string;
    date_to?: string;
    period?: string;
    team_ids?: string;
    team_display?: {
      name: string;
      color?: string;
    }[];
  };
  dataDisplay: ChartSeriesConfig[];
  chartItems: ChartItemData[];
}

/** Escape a single CSV field value. Handles RFC 4180 quoting and formula-injection. */
export function sanitizeCsvValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  const str = String(value);

  // Formula-injection protection: prefix values that open with a spreadsheet operator.
  const injectionLeaders = ['=', '+', '-', '@', '\t', '\r'];
  const prefixed = injectionLeaders.some((c) => str.startsWith(c)) ? `'${str}` : str;

  // RFC 4180: wrap in double-quotes if the value contains a comma, double-quote, or newline.
  if (
    prefixed.includes('"') ||
    prefixed.includes(',') ||
    prefixed.includes('\n') ||
    prefixed.includes('\r')
  ) {
    return `"${prefixed.replace(/"/g, '""')}"`;
  }
  return prefixed;
}

/** Join sanitized values into a single CSV row string. */
export function buildCsvRow(values: unknown[]): string {
  return values.map(sanitizeCsvValue).join(',');
}

/** Return only the series that are currently visible on the chart. */
export function getVisibleSeries(
  dataDisplay: ChartSeriesConfig[],
  chartType: string
): ChartSeriesConfig[] {
  return dataDisplay.filter(
    (s) => !s.disabled && (s.checked || s.fixed || s.fixedFor?.includes(chartType))
  );
}

/** Map a series key to the actual property name used in a chartItems object. */
export function getDataKeyForSeries(seriesKey: string): string {
  return COUNT_ALIAS_KEYS.has(seriesKey) ? 'count' : seriesKey;
}

/** True when at least one of the given series has a value > 0 across the items. */
function seriesHaveNonZeroValues(
  visibleSeries: ChartSeriesConfig[],
  chartItems: ChartItemData[]
): boolean {
  return visibleSeries.some((s) => {
    const dataKey = getDataKeyForSeries(s.key);
    return chartItems.some((item) => (item[dataKey] as number) > 0);
  });
}

/**
 * True when the chart has at least one visible series with a non-zero value.
 * A chart can render with items (one per day in the range) while every value is
 * 0 — it looks empty. Use this to block exports of such a chart.
 */
export function hasVisibleNonZeroData(
  dataDisplay: ChartSeriesConfig[],
  chartType: string,
  chartItems: ChartItemData[] | null | undefined
): boolean {
  if (!chartItems?.length) return false;
  const visibleSeries = getVisibleSeries(dataDisplay, chartType);
  if (visibleSeries.length === 0) return false;
  return seriesHaveNonZeroValues(visibleSeries, chartItems);
}

/**
 * Build a complete chart CSV string from current chart state.
 * Returns `{ ok: false, reason }` for any pre-flight condition that prevents export.
 */
export function buildChartCsv(options: ChartCsvOptions): CsvExportResult {
  const { chartTitle, chartDataType, chartType, filters, dataDisplay, chartItems } = options;

  if (!chartItems?.length) {
    return { ok: false, reason: 'no_data' };
  }

  const visibleSeries = getVisibleSeries(dataDisplay, chartType);
  if (visibleSeries.length === 0) {
    return { ok: false, reason: 'no_data' };
  }

  if (!seriesHaveNonZeroValues(visibleSeries, chartItems)) {
    return { ok: false, reason: 'no_data' };
  }

  const dataPoints = chartItems.length * visibleSeries.length;
  if (dataPoints > CHART_CSV_MAX_DATA_POINTS) {
    return { ok: false, reason: 'data_point_limit_exceeded' };
  }

  const now = new Date().toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

  const dateRange =
    filters.date_from && filters.date_to
      ? `${filters.date_from} to ${filters.date_to}`
      : 'All time';

  const teamDisplayNames = filters.team_display?.map((team) => team.name).filter(Boolean);
  const teamFilter =
    teamDisplayNames && teamDisplayNames.length > 0
      ? `Teams: ${teamDisplayNames.join(', ')}`
      : filters.team_ids && filters.team_ids.trim().length > 0
      ? `Teams: ${filters.team_ids}`
      : 'All managed teams';

  const seriesLabels = visibleSeries.map((s) => s.label || s.key).join(', ');

  const lines: string[] = [
    `# ${chartTitle}`,
    `# Generated on ${now}`,
    `# Chart: ${chartDataType}`,
    `# Date Range: ${dateRange}`,
    `# Granularity: ${filters.period || 'day'}`,
    `# Series: ${seriesLabels}`,
    `# Filters: ${teamFilter}`,
    '',
  ];

  // Header row
  lines.push(buildCsvRow(['date', ...visibleSeries.map((s) => s.label || s.key)]));

  // Data rows
  for (const item of chartItems) {
    const rowValues: unknown[] = [item.date];
    for (const series of visibleSeries) {
      const dataKey = getDataKeyForSeries(series.key);
      rowValues.push(item[dataKey] ?? 0);
    }
    lines.push(buildCsvRow(rowValues));
  }

  const csv = lines.join('\n');

  const byteLength = getUtf8ByteLength(csv);
  if (byteLength > CHART_CSV_MAX_BYTES) {
    return { ok: false, reason: 'file_size_exceeded' };
  }

  return { ok: true, csv };
}

function getUtf8ByteLength(str: string): number {
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(str).length;
  }
  // Node.js / JSDOM test environment fallback
  return Buffer.byteLength(str, 'utf8');
}

/** Create a Blob URL, trigger a browser download, then revoke the URL. */
export function downloadCsvBlob(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/** Build a safe filename for the downloaded CSV. */
export function buildCsvFilename(chartDataType: string): string {
  const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  const safeName = chartDataType.toLowerCase().replace(/[^a-z0-9]+/g, '_');
  return `analytics_${safeName}_${date}.csv`;
}

/** Human-readable chart title used in CSV metadata. */
export const CHART_TITLE_MAP: Record<string, string> = {
  users: 'Licensed users with activity',
  activities: 'Activities',
  videos: 'Number of videos initiated',
  videos_overall: 'Video time watched',
  quizzes: 'Quizzes',
  labs: 'Hands-on labs',
};
